'use client'

import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'

export function RotatingGlobeSection() {
  const isMobile = useIsMobile()
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: isMobile ? 20 : 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: isMobile ? 0.3 : 0.6 }}
      className={cn(
        "mt-16 md:mt-32 mb-16 rounded-3xl p-6 md:p-12 border relative",
        isMobile 
          ? "bg-slate-900/95 border-blue-500/30" 
          : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
      )}
      style={isMobile ? {
        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
      } : {
        boxShadow: `
          0 8px 32px 0 rgba(31, 38, 135, 0.1),
          0 0 0 1px rgba(255, 255, 255, 0.1) inset,
          0 0 60px rgba(59, 130, 246, 0.1)
        `,
      }}
    >
      {!isMobile && (
        <div 
          className="absolute inset-0 rounded-3xl border-2 border-blue-400/30 dark:border-blue-500/20"
          style={{
            animation: 'hero-glow-pulse 3s ease-in-out infinite',
            boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)',
          }}
        />
      )}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div className="order-2 lg:order-1">
          <h2 className="text-3xl md:text-4xl font-semibold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 to-blue-600">
            Nationwide Opportunities
          </h2>
          <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
            We have opportunities from around America across every level of government—federal contracts, 
            state and local bids, grants, and small business innovation programs. Our platform aggregates 
            them all in one place so you never miss a match.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-primary mb-1">Federal Contracts</h3>
                <p className="text-muted-foreground text-sm">
                  All active solicitations from SAM.gov across every federal agency—DoD, HHS, GSA, and more
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-primary mb-1">State Contracts</h3>
                <p className="text-muted-foreground text-sm">
                  Opportunities from all 50 states and territories
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-primary mb-1">Local Contracts</h3>
                <p className="text-muted-foreground text-sm">
                  City, county, and municipal government opportunities nationwide
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-primary mb-1">Education Contracts</h3>
                <p className="text-muted-foreground text-sm">
                  K-12, higher education, and educational institution opportunities across America
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-primary mb-1">Grants & SBIR/STTR</h3>
                <p className="text-muted-foreground text-sm">
                  Federal grants from Grants.gov and SBIR/STTR small business innovation solicitations
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2 h-[300px] md:h-[400px] w-full relative rounded-xl overflow-hidden">
          {/* Glow effect container - simplified on mobile */}
          <div className="absolute inset-0 z-0">
            {isMobile ? (
              /* Mobile: Simple static gradient glow */
              <div 
                className="absolute inset-0"
                style={{
                  background: 'radial-gradient(circle at center, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
                }}
              />
            ) : (
              /* Desktop: Animated glow orbs */
              <>
                <motion.div
                  className="absolute top-1/4 left-1/4 w-32 h-32 bg-blue-500/40 rounded-full blur-2xl"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.4, 0.6, 0.4],
                    x: [0, 20, 0],
                    y: [0, -15, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.div
                  className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-sky-400/40 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.5, 0.3],
                    x: [0, -25, 0],
                    y: [0, 20, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
                <motion.div
                  className="absolute top-1/2 right-1/3 w-36 h-36 bg-blue-600/30 rounded-full blur-2xl"
                  animate={{
                    scale: [1, 1.4, 1],
                    opacity: [0.3, 0.5, 0.3],
                    x: [0, 15, 0],
                    y: [0, 25, 0],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2,
                  }}
                />
                <motion.div
                  className="absolute bottom-1/3 left-1/3 w-28 h-28 bg-sky-500/35 rounded-full blur-2xl"
                  animate={{
                    scale: [1, 1.6, 1],
                    opacity: [0.35, 0.55, 0.35],
                    x: [0, -20, 0],
                    y: [0, -20, 0],
                  }}
                  transition={{
                    duration: 4.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.5,
                  }}
                />
              </>
            )}
          </div>

          {/* US Map Image with glow effect */}
          <motion.div
            className="absolute inset-0 z-10 flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: isMobile ? 1 : 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: isMobile ? 0.3 : 0.8 }}
          >
            <div className="relative w-full h-full">
              {/* Glow ring around image - simplified on mobile */}
              {!isMobile && (
                <motion.div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
                    filter: 'blur(20px)',
                  }}
                  animate={{
                    opacity: [0.3, 0.5, 0.3],
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              )}
              
              {/* Image with glow shadow */}
              <img
                src="/us.png"
                alt="United States Map"
                className={cn(
                  "relative z-10 w-full h-full object-contain",
                  !isMobile && "drop-shadow-[0_0_30px_rgba(59,130,246,0.4)]"
                )}
                style={!isMobile ? {
                  filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.3))',
                } : undefined}
              />
              
              {/* Pulsing border glow - only on desktop */}
              {!isMobile && (
                <motion.div
                  className="absolute inset-0 rounded-xl border-2 border-blue-500/30"
                  animate={{
                    boxShadow: [
                      '0 0 20px rgba(59, 130, 246, 0.3)',
                      '0 0 40px rgba(59, 130, 246, 0.5)',
                      '0 0 20px rgba(59, 130, 246, 0.3)',
                    ],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}
